/**
 * Жидкий single-source-of-truth для «Новой продажи».
 *
 * Один `newSaleAtom` хранит вообще всё про текущий kasse-flow:
 *   • `step` — на каком шаге мы (event / scheme / cart);
 *   • `eventId`, `sessionId` — выбранные событие/сеанс (после шагов);
 *   • `reservationExpiresAt` — unix-ms когда серверная резервация
 *     истекает (UI считает оставшиеся секунды от Date.now()).
 *
 * Корзина (basket items) живёт не здесь — она серверная истина и
 * берётся через `getActiveOrder` (react-query). В atom только то,
 * что нужно UI-навигации.
 *
 * Этим подходом мы следуем v2 (`src/shared/lib/new-sale.ts`):
 * единственный atom + actions-хук вместо россыпи 32 атомов как в
 * легаси.
 */

import {atom} from 'jotai';

/**
 * Шагов всего 3: Событие → Зал → Корзина.
 * Сессия — это модалка поверх шага «Событие» (см. `SessionsModal`),
 * не отдельный шаг — поэтому в типе её нет.
 */
export type NewSaleStep = 'event' | 'scheme' | 'cart';

export type NewSaleState = {
  step: NewSaleStep;
  eventId: number | null;
  sessionId: number | null;
  /**
   * Активный сектор многосекторной схемы. null — ещё не выбран (показ
   * `SectorPickerModal`) или схема плоская. Хранится в atom'е, чтобы
   * сохраняться между ре-открытиями drawer'а.
   */
  selectedSectorId: number | null;
  /** unix-ms истечения серверной брони. null если корзина пуста. */
  reservationExpiresAt: number | null;
};

export const INITIAL_NEW_SALE_STATE: NewSaleState = {
  step: 'event',
  eventId: null,
  sessionId: null,
  selectedSectorId: null,
  reservationExpiresAt: null,
};

/** Единственный источник правды UI новой продажи. */
export const newSaleAtom = atom<NewSaleState>(INITIAL_NEW_SALE_STATE);

/**
 * Открыт ли drawer кассы. Глобальный — чтобы открывать с любой страницы
 * (кнопка на Home, хоткей в шапке, кнопки в EventDetails и т.д.).
 * Сам Drawer смонтирован один раз в `AppShell`.
 */
export const isNewSaleOpenAtom = atom<boolean>(false);

/**
 * Открыта ли модалка выбора сеанса (поверх drawer'а кассы). По паттерну
 * v2 + легаси: послеклика по событию открывается Modal со списком
 * сеансов, выбор → переход на шаг `scheme`. Крестик — отмена выбора,
 * остаёмся на шаге «Событие».
 */
export const isSessionsModalOpenAtom = atom<boolean>(false);

/**
 * Pan-режим для SVG-схемы. `true` — drag тянет схему (для зум-навигации);
 * `false` — drag = rect-select для multi-выбора мест.
 *
 * Атом отдельный — потому что toggle живёт в header'е drawer'а, а
 * читает его `SchemeViewport` глубоко внутри. Через атом проще, чем
 * пробрасывать props через 4 уровня.
 *
 * По умолчанию OFF (rect-select) — это основной кассовый режим, pan
 * включается явно когда нужна зум-навигация.
 */
export const isPanModeAtom = atom<boolean>(false);

/**
 * Frame-режим для тач-устройств. На мобилке drag-rect библиотеки
 * `react-mouse-select` не работает — touch-жест забирает pan/zoom
 * у `react-zoom-pan-pinch`. Поэтому multi-select на тач реализован
 * паттерном «два тапа = два угла прямоугольника» (паритет с v3
 * `SchemeCanvas`).
 *
 * Включается кнопкой в header'е (только на мобилке). Когда `true`,
 * `SchemeViewport` собирает 2 угла, на втором вызывает `onMultiSelect`.
 */
export const isFrameSelectAtom = atom<boolean>(false);

/**
 * Какой именно flow сейчас рендерится в `SchemeStep`. Header читает
 * это значение, чтобы решить какие кнопки показать:
 *
 *   • `scheme`  → есть SVG-схема (FlatScheme / Sectored / Outer) →
 *                 показываем PanModeToggle + FrameSelectToggle;
 *   • `entry`   → входные билеты (без SVG) → прячем pan/frame,
 *                 показываем кнопку «Очистить корзину» (FAB'а нет);
 *   • `info`    → информационный сеанс → ничего лишнего;
 *   • `null`    → SchemeStep ещё не смонтирован (other steps).
 *
 * Каждый flow в `useEffect` ставит свой kind при mount и сбрасывает
 * на `null` при unmount.
 */
export type SchemeFlowKind = 'scheme' | 'entry' | 'info' | null;
export const schemeFlowKindAtom = atom<SchemeFlowKind>(null);
